import { useCallback, useEffect, useMemo, useState } from "react";
import type { SignUpData } from "types/auth";
import StepTipoDocumento from "./steps/StepTipoDocumento";
import StepAccount from "./steps/StepAccount";
import StepProfile from "./steps/StepProfile";
import StepAddress from "./steps/StepAddress";
import StepIOR from "./steps/StepIor";
import StepLocalFixo from "./steps/StepLocalFixo";

import "../../styles/cadastro.css"; 
import { registrarCliente, RegistrarPayload, validarCNPJ } from "services/api";
import LanguageSelect from "app/componentes/LanguageSwitcher";
import { useNavigate } from "react-router-dom";

const LS_KEY = "signup_draft";

const initial: SignUpData = {
  tipoDocumento: "cnpj",
  email: "", senha: "", confirmarSenha: "", tipoDeConta: "empresa",
  razaoSocial: "", telefoneCelular: "",
  enderecoEmpresa: { pais: "", rua: "", numero: "", cidade: "", estado: "", cep: "" },
  cnpj: "", plano: "basico"
};

export default function SignUpWizard() {
  const [data, setData] = useState<SignUpData>(initial);
  const [step, setStep] = useState(0);
  const [, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) setData({ ...initial, ...JSON.parse(raw) });
  }, []);

  useEffect(() => {
    localStorage.setItem(LS_KEY, JSON.stringify(data));
  }, [data]);

  const next = useCallback((partial: Partial<SignUpData>) => {
    setData(prev => ({ ...prev, ...partial }));
    setStep(s => s + 1);
  }, []);

  const back = useCallback(() => {
    setStep(s => Math.max(0, s - 1));
  }, []);

  const onFinish = useCallback(async (partial?: Partial<SignUpData>) => {
    setLoading(true);
    const m = partial ? { ...data, ...partial } : data;

    try {
      const payload: RegistrarPayload = {
        emailPrincipal: m.email,
        senha: m.senha || "",
        tipoConta: m.tipoDeConta === "parceiro" ? "parceiro" : "empresa",
        emailAssociado: m.usuarioAssociado || m.email,
        codigo: "CLI-" + Date.now(),
        razaoSocial: m.razaoSocial,
        enderecoPais: m.enderecoEmpresa.pais,
        enderecoCEP: m.enderecoEmpresa.cep,
        enderecoRua: m.enderecoEmpresa.rua,
        enderecoNumero: m.enderecoEmpresa.numero,
        enderecoComplemento: m.enderecoEmpresa.complemento,
        enderecoCidade: m.enderecoEmpresa.cidade,
        enderecoEstado: m.enderecoEmpresa.estado,
        tipoDocumento: m.tipoDocumento || "cnpj",
        cnpj: m.tipoDocumento === "estrangeiro"
          ? String(m.cnpj || "").trim()
          : String(m.cnpj || "").replace(/\D/g, ""),
        cnaePrincipal: m.tipoDocumento === "cnpj"
          ? (m.cnaePrincipal || "").replace(/\D/g, "")
          : "",
        telefoneCelular: (m.telefoneCelular || "").replace(/\D/g, ""),
        plano: m.plano || "basico",
        descIOR: m.descIOR, nomeIOR: m.nomeIOR, emailIOR: m.emailIOR,
        tipoIOR: m.tipoIOR, paisIOR: m.paisIOR, estadoIOR: m.estadoIOR,
        cidadeIOR: m.cidadeIOR, enderecoIOR: m.enderecoIOR, numeroIOR: m.numeroIOR,
        cod_postalIOR: m.cod_postalIOR, telefoneIOR: m.telefoneIOR, state_tax_idIOR: m.state_tax_idIOR,
        destinoFixoNome: m.destinoFixoNome, destinoFixoPais: m.destinoFixoPais,
        destinoFixoEstado: m.destinoFixoEstado, destinoFixoCidade: m.destinoFixoCidade,
        destinoFixoRua: m.destinoFixoRua, destinoFixoCEP: m.destinoFixoCEP,
        destinoFixoTelefone: m.destinoFixoTelefone,
      };

      const { data: resp } = await registrarCliente(payload);

      if ("erro" in resp) {
        setErro(resp.erro ?? "Erro ao processar cadastro"); // CORREÇÃO TS2345
        return;
      }

      alert("CADASTRO REALIZADO COM SUCESSO");
      localStorage.removeItem(LS_KEY);
      navigate("/login");
    } catch (e: any) {
      const msg = e?.response?.data?.erro || e?.response?.data?.message || e?.message || "Falha de rede.";
      setErro(msg);
    } finally {
      setLoading(false);
    }
  }, [data, navigate]);

  const steps = useMemo(() => {
    const list = [
      () => <StepTipoDocumento data={data} onNext={next} />,
      () => <StepAccount data={data} onNext={next} />,
      () => <StepAddress data={data} onNext={next} onBack={back} />,
    ];

    if (data.tipoDocumento === "cpf") {
      list.push(() => <StepLocalFixo data={data} onNext={next} onBack={back} />);
    }

    list.push(
      () => <StepIOR data={data} onNext={next} onBack={back} />,
      () => <StepProfile data={data} onFinish={onFinish} onBack={back} />
    );

    return list;
  }, [data, next, back, onFinish]);

  const safeStep = Math.min(step, steps.length - 1);
  const CurrentStep = steps[safeStep];

  return (
    <div className="cadastro-pagina">
      <div className="bg-grid"></div>
      <div className="bg-blob blob-left"></div>
      <div className="bg-blob blob-right"></div>
      <div className="bg-blob blob-center"></div>

      <section className="cadastro-cartao">
        <div className="lang-container">
          <LanguageSelect />
        </div>

        <img src="/images/intrex_logo.png" alt="Intrex" className="cadastro-logo-header" />

        <div className="auth-switcher">
          <button onClick={() => navigate('/login')} className="switch-btn">Login</button>
          <button className="switch-btn active">Create Account</button>
        </div>

        <div className="cadastro-progresso">
          <span className="cadastro-progresso__preenchimento" style={{ width: `${((safeStep + 1) / steps.length) * 100}%` }} />
        </div>

        <div className="cadastro-etapa-label">
          STEP {safeStep + 1} OF {steps.length}
        </div>

        <CurrentStep />
        {erro && <div className="cadastro-erro" style={{ marginTop: "0.75rem" }}>{erro}</div>}
      </section>
    </div>
  );
}